import { Card, PanelProps, Spinner, SpinnerSize } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { getDataSourceCardTestId } from 'src/constants/testIds';
import {
  DataSourceCreationPanel,
  DataSourceCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/DataSourceCreationPanel/DataSourceCreationPanel';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import {
  ProjectVirtualGraphCreationPanel,
  ProjectVirtualGraphCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/ProjectVirtualGraphCreationPanel';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { DataSource } from 'src/store/state/dataSources';
import { components } from 'src/templates/copy';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DataSourceSelectionPanelProps = ProjectResourceDialogPanelProps & {
  type: DataSourceType;
};

export const CREATE_NEW_DATA_SOURCE = {
  id: 'CREATE_NEW_DATA_SOURCE',
  options: { 'jdbc.url': '', 'jdbc.username': '' },
};

export const DataSourceSelectionPanel: React.VFC<
  PanelProps<DataSourceSelectionPanelProps>
> = ({ closePanel, openPanel, setFooterProps, type }) => {
  const isMounted = useIsMounted();
  const dispatch = useReduxDispatch();

  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);

  const dataSources = React.useMemo(
    () => dataSourcesByType[type] || [],
    [dataSourcesByType, type]
  );
  const isPendingOptions = React.useMemo(() => {
    return dataSources.some((dataSource) => !dataSource.options);
  }, [dataSources]);

  const handleRefreshTables = React.useCallback(
    (dataSourceId: string) => {
      dispatch(refreshDataSourceTables(dataSourceId));
    },
    [dispatch]
  );

  const handleSelectionOfCreateNewDataSource = React.useCallback(() => {
    const props: DataSourceCreationPanelProps = { setFooterProps, type };
    openPanel({
      renderPanel: DataSourceCreationPanel,
      props,
    });
  }, [openPanel, setFooterProps, type]);

  const handleSelectionOfDataSource = React.useCallback(
    (selectedDataSource: DataSource) => {
      const props: ProjectVirtualGraphCreationPanelProps = {
        setFooterProps,
        dataSourceId: selectedDataSource.id,
      };
      handleRefreshTables(selectedDataSource.id);
      openPanel({
        renderPanel: ProjectVirtualGraphCreationPanel,
        props,
      });
    },
    [handleRefreshTables, openPanel, setFooterProps]
  );

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        handleBackButtonClick: closePanel,
      });
    }
  }, [closePanel, isMounted, setFooterProps]);

  const cardList: React.ReactElement[] = [
    CREATE_NEW_DATA_SOURCE,
    ...dataSources,
  ].map((dataSource, idx) => {
    return (
      <Card
        key={dataSource.id}
        className={classNames({
          'sd-data-source-panel-cards__card': true,
          'sd-data-source-panel-cards__card--source': true,
          'sd-data-source-panel-cards__card--create-new':
            dataSource === CREATE_NEW_DATA_SOURCE,
        })}
        data-testid={getDataSourceCardTestId(dataSource.id, idx)}
        interactive
        onClick={() => {
          if (dataSource === CREATE_NEW_DATA_SOURCE) {
            handleSelectionOfCreateNewDataSource();
          } else {
            handleSelectionOfDataSource(dataSource as DataSource);
          }
        }}
      >
        <b>
          <Text ellipsize>
            {dataSource === CREATE_NEW_DATA_SOURCE
              ? components.projectResourceDialog.projectDataSourcePanel
                  .createNewDataSource
              : dataSource.id}
          </Text>
        </b>
        {dataSource === CREATE_NEW_DATA_SOURCE ? null : (
          <>
            <Text ellipsize>
              {components.projectResourceDialog.projectDataSourcePanel.endpoint}
              : {dataSource.options?.['jdbc.url'] || ''}
            </Text>
            <Text ellipsize>
              {components.projectResourceDialog.projectDataSourcePanel.username}
              : {dataSource.options?.['jdbc.username'] || ''}
            </Text>
          </>
        )}
      </Card>
    );
    // TODO: Need a more general approach to showing url / username
    // information when non-JDBC data sources are supported
  });

  return (
    <DataSourcePanelContainer type={type}>
      {isPendingOptions ? (
        <Spinner size={SpinnerSize.LARGE} />
      ) : (
        <div
          className={classNames('sd-dialog-tile', 'sd-data-source-panel-cards')}
        >
          {cardList}
        </div>
      )}
    </DataSourcePanelContainer>
  );
};
